/**
 * Попап ШВИДКОГО створення номенклатури (2026-07-31, рішення власника):
 * з редакторів документів (замовлення/рахунок) — лише найнеобхідніші поля
 * (найменування, вид, ставка ПДВ, артикул, УКТЗЕД); решта реквізитів
 * правиться у повній картці номенклатури. Код — авто (NOM-N).
 *
 * ч.1275: `initial` — дані рядка документа, з якого створюють картку (сторінка розпізнавання
 * вхідного документа): назва і артикул постачальника, ставка ПДВ рядка, послуга чи товар,
 * одиниця виміру з документа. Без `initial` попап порожній, як і раніше.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, type Nomenclature } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { invalidateRefLists } from '@/shared/utils/refLists';

export interface QuickNomenclatureInitial {
  name?: string | null;
  article?: string | null;
  vatRateId?: string | null;
  /** послуга → одразу вид «Послуга»; для товару вид обирає користувач */
  isService?: boolean;
  /** одиниця виміру з документа → базова одиниця картки */
  baseUomId?: string | null;
  /** як одиниця записана в документі — лише для підказки */
  unitLabel?: string | null;
}

export const NAME_MAX = 500;
export const ARTICLE_MAX = 50;

export default function QuickNomenclatureModal({ onCreated, onClose, initial }: {
  onCreated: (nom: Nomenclature) => void;
  onClose: () => void;
  initial?: QuickNomenclatureInitial;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(() => (initial?.name ?? '').trim().slice(0, NAME_MAX));
  const [kindId, setKindId] = useState('');
  const [kindTouched, setKindTouched] = useState(false);
  const [article, setArticle] = useState(() => (initial?.article ?? '').trim().slice(0, ARTICLE_MAX));
  const [uktZed, setUktZed] = useState('');
  const [vatRateId, setVatRateId] = useState(initial?.vatRateId ?? '');
  const [error, setError] = useState<string | null>(null);
  const baseUomId = initial?.baseUomId ?? null;

  const kindsQ = useQuery({
    queryKey: ['nomenclature-kinds'],
    queryFn: refs.nomenclatureKinds.list,
  });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const kind = (kindsQ.data ?? []).find((k) => k.id === kindId);
  const isService = kind?.is_service ?? false;

  // Послуга з документа → вид «Послуга» (дефолтний вид з кодом SERVICE, інакше перший вид-послуга).
  useEffect(() => {
    if (kindTouched || kindId || !initial?.isService || !kindsQ.data) return;
    const live = kindsQ.data.filter((k) => k.is_service && !k.deletion_mark);
    const svc = live.find((k) => k.code === 'SERVICE') ?? live[0];
    if (svc) setKindId(svc.id);
  }, [kindsQ.data, initial?.isService, kindId, kindTouched]);

  const create = useMutation({
    mutationFn: () => nomApi.create({
      code: null,
      article: article.trim() || null,
      name: name.trim(),
      full_name: null,
      is_service: isService,
      is_excise: false,
      ukt_zed_code: (!isService && uktZed.trim()) ? uktZed.trim() : null,
      country_origin_code: null,
      base_uom_id: baseUomId,
      vat_rate_id: vatRateId || null,
      default_cost_item_id: null,
      kind_id: kindId || null,
      account_id: null,
      transfer_account_id: null,
      off_balance_account_id: null,
      off_balance_transfer_account_id: null,
      tax_purpose_id: null,
      income_cost_tax_purpose_id: null,
    }),
    onSuccess: (nom) => {
      // Усі кеші списків номенклатури (редактори використовують різні ключі).
      invalidateRefLists(qc, 'nomenclature');   // ч.1267: уся родина ключів, а не два з них
      onCreated(nom);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div className="card" style={{ width: 520, margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Нова номенклатура (швидко)</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="field">
          <label>Найменування <span style={{ color: 'red' }}>*</span></label>
          <input value={name} autoFocus maxLength={NAME_MAX}
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Вид номенклатури <span style={{ color: 'red' }}>*</span></label>
            <select value={kindId} onChange={(e) => { setKindTouched(true); setKindId(e.target.value); }}>
              <option value="">— оберіть —</option>
              {(kindsQ.data ?? []).map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Ставка ПДВ</label>
            <select value={vatRateId} onChange={(e) => setVatRateId(e.target.value)}>
              <option value="">— з документа —</option>
              {(vatRatesQ.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Артикул</label>
            <input value={article} maxLength={ARTICLE_MAX} onChange={(e) => setArticle(e.target.value)} />
          </div>
          {!isService && (
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Код УКТ ЗЕД</label>
              <input value={uktZed} placeholder="для товарів (у ПН)"
                onChange={(e) => setUktZed(e.target.value)} />
            </div>
          )}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.25rem 0 0.6rem' }}>
          Код призначиться автоматично.{' '}
          {baseUomId
            ? <>Одиниця виміру — з документа{initial?.unitLabel ? <> («{initial.unitLabel}»)</> : null}. Рахунки
              обліку та інші реквізити — у повній картці номенклатури.</>
            : <>Одиниця виміру, рахунки обліку та інші реквізити — у повній картці номенклатури.</>}
        </p>
        {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary"
            disabled={!name.trim() || !kindId || create.isPending}
            title={!name.trim() ? 'Вкажіть найменування'
              : !kindId ? 'Оберіть вид номенклатури' : undefined}
            onClick={() => create.mutate()}>
            {create.isPending ? 'створюється…' : 'створити і підставити'}
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}
